import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { ClientsService } from './clients.service';
import { ClientSearchOptionsResponse } from './models/client-search-options-response.model';
import { catchError } from 'rxjs/operators';
import { SearchOptionRequest } from 'app/shared/models/request/search-option-request.model';
import { AuditorService } from '../auditor/auditor.service';
import { SettingsService } from '../admin/settings/settings.service';
import { AuthService } from 'app/core/auth/auth.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { AlertService } from 'app/shared/services/alert.service';
import { webpages_Role } from 'app/shared/models/domain/data.model';
import { MmrService } from '../admin/personnel/admin-mmr/mmr.service';
import { ClientToMmrViewService } from 'app/shared/services/client-to-mmr-view.service';
import { PageRequest } from 'app/shared/models/request/page-request.model';
import { AdminSalesRepService } from '../admin/personnel/admin-sales-rep/admin-sales-rep.service';

@Injectable({
    providedIn: 'root'
})
export class ClientSearchOptionsResolver {
    constructor(private _clientsService: ClientsService) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this._clientsService.getClientSearchOptions();
    }
}

@Injectable({
    providedIn: 'root'
})
export class ExpenseOptionsResolver {
    constructor(private _settingService: SettingsService) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this._settingService.getAuditorExpenseReportOptionsList({ currentPage: 1, itemsPerPage: 1000 });
    }
}


@Injectable({
    providedIn: 'root'
})
export class AuthorizedSignerListOptionsResolver {
    constructor(private _clientsService: ClientsService) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this._clientsService.getAuthorizedSignerListOptions();
    }
}

@Injectable({
    providedIn: 'root'
})
export class ClientsContactResolver {
    constructor(
        private _clientsService: ClientsService,
        private _router: Router,
        private alertService: AlertService
    ) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this._clientsService.getClientById(route.paramMap.get('id'))
            .pipe(
                catchError((error) => {
                    const parentUrl = state.url.split('/').slice(0, -1).join('/');
                    const isMmr = route.data.isMmrLoggedIn;
                    if (isMmr) {
                        this._router.navigateByUrl('/mmr/clients/list');
                        this.alertService.error('Client Is Not Accessible');
                    } else {
                        this._router.navigateByUrl(parentUrl);
                    }
                    return throwError(error);
                })
            );
    }
}

@Injectable({
    providedIn: 'root'
})
export class ClientsNotesResolver {
    constructor(
        private _clientsService: ClientsService,
        private _router: Router
    ) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this._clientsService.getClientNotesByClientId(route.paramMap.get('id'))
            .pipe(
                catchError((error) => {
                    const parentUrl = state.url.split('/').slice(0, -1).join('/');
                    this._router.navigateByUrl(parentUrl);
                    return throwError(error);
                })
            );
    }
}

@Injectable({
    providedIn: 'root'
})
export class ClientStandardsResolver {
    constructor(private _clientsService: ClientsService) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this._clientsService.getClientStandards(route.paramMap.get('id'));
    }
}

@Injectable({
    providedIn: 'root'
})
export class ClientMMRListResolver {
    constructor(private readonly mmrService: MmrService) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this.mmrService.getMmrList({
            currentPage: 1,
            itemsPerPage: environment.defaultAllItemsPerPage,
            loginAccess: 1,
        });
    }
}


@Injectable({
    providedIn: 'root'
})
export class ClientStandardsViewResolver {
    constructor(private _clientsService: ClientsService) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this._clientsService.getClientStandardsView(route.paramMap.get('id'));
    }
}

@Injectable({
    providedIn: 'root'
})
export class ClientDataResolver {
    constructor(
        private _clientsService: ClientsService,
        private _router: Router,
        private _authService: AuthService,
    ) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this._clientsService.getClientByIdForClient(this._authService?.localClientId);
    }
}


@Injectable({
    providedIn: 'root'
})
export class ClientStandardsForClientResolver {
    constructor(
        private _clientsService: ClientsService,
        private _authService: AuthService,
    ) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this._clientsService.getClientStandardsForClient(this._authService?.localClientId);
    }
}

@Injectable({
    providedIn: 'root'
})
export class ClientNotesForClientResolver {
    constructor(
        private _clientsService: ClientsService,
        private _authService: AuthService,
        private _router: Router,
    ) {
    }

    resolve(
        route: ActivatedRouteSnapshot
        , state: RouterStateSnapshot
    ): Observable<any> {
        const roles = this._authService.localUserRoles;
        let isMmr = false;

        roles.forEach((role: webpages_Role) => {
            if (environment.roleTabs['mmr'].includes(role.roleName)) {
                isMmr = true;
            }
        });
        const idToUse = isMmr ? this._authService.localMmrId : this._authService.localClientId;

        return this._clientsService.getClientNotesByClientId(idToUse, this._authService.localUser?.userId)
            .pipe(
                catchError((error) => {
                    const parentUrl = state.url.split('/').slice(0, -1).join('/');
                    this._router.navigateByUrl(parentUrl);
                    return throwError(error);
                })
            );
    }
}

@Injectable({
    providedIn: 'root'
})
export class ClientStandardsViewForClientResolver {
    constructor(
        private _clientsService: ClientsService,
        private _authService: AuthService,

    ) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this._clientsService.getClientStandardsViewForClient(this._authService?.localClientId);
    }
}

@Injectable({
    providedIn: 'root'
})
export class ClientChangeLogDataResolver {
    constructor(
        private _clientsService: ClientsService,
        private _http: HttpClient,
        private _authService: AuthService,
    ) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this._http.get(environment.apiBaseUrl + 'api/data/client/activity', {
            params: {
                'sort[created]': 'desc',
                'currentPage': 1,
                'find[subjectId]': this._authService?.localClientId,
                'find[subjectType:in]': ['Client', 'ClientAdditionalSite', 'QuoteRequestLocation', 'ClientAttachment'].join(),
                'find[activityType:in]': ['clientUpdate', 'clientSiteCreate', 'clientSiteUpdate', 'clientQuoteRequestLocationUpdate', 'Update', 'update', 'clientAttachment', 'Create', 'create'].join(),
                'find[activityStatus]': 'new',
                'itemsPerPage': environment.defaultItemsPerPage,
            }
        });
    }
}

@Injectable({
    providedIn: 'root'
})
export class ClientMmrsResolver {
    constructor(
        private readonly _clientToMmrViewService: ClientToMmrViewService,
        private readonly _router: Router
    ) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        const clintToMmrRequest = new PageRequest();
        clintToMmrRequest.currentPage = 1;
        clintToMmrRequest.itemsPerPage = environment.defaultItemsPerPage;
        clintToMmrRequest.find['clientId'] = route.paramMap.get('id');
        clintToMmrRequest.sort = 'created';
        clintToMmrRequest.sortDir = 'desc';
        return this._clientToMmrViewService.get(clintToMmrRequest)
            .pipe(
                catchError((error) => {
                    const parentUrl = state.url.split('/').slice(0, -1).join('/');
                    this._router.navigateByUrl(parentUrl);
                    return throwError(error);
                })
            );
    }
}

@Injectable({
    providedIn: 'root'
})
export class SalesRepDetailsResolver {
    constructor(private readonly _salesRepService: AdminSalesRepService,
        private readonly authService: AuthService
    ) {
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        return this._salesRepService.getSalesRepDetailsByUserId(this.authService.localUser.userId);
    }
}